import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  LockClosedIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CANVAS_DIMENSIONS } from '../../../constants';
import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import { editorUiAtom } from '../../../store/atoms/editorUiAtom';
import { slideAtom, type VariantKey } from '../../../store/atoms/slideAtom';

/**
 * Overlay de loading do preview — cobre a área (inclusive um preview anterior)
 * com um spinner claro enquanto o conteúdo da variante está sendo carregado.
 */
function PreviewLoadingOverlay() {
  const { t } = useTranslation('slideEditor');
  return (
    <div className="absolute inset-none z-20 flex items-center justify-center bg-base-brand-900">
      <div
        role="status"
        aria-label={t('preview.loading')}
        className="h-mini w-mini animate-spin rounded-circular border-thin border-base-neutral-white border-t-base-neutral-none"
      />
    </div>
  );
}

export function PreviewTab() {
  const { t } = useTranslation('slideEditor');
  const ctx = useSlideEditorContext();
  const [slide] = useAtom(slideAtom);
  const [uiState] = useAtom(editorUiAtom);

  const activeDevice = uiState.activeDevice;

  // Variante ativa — usada como dep do effect de preview para re-capturar
  // o canvas quando o conteúdo muda (load da API, edições, troca de idioma/device).
  const activeVariantKey = `${uiState.activeLanguage}-${activeDevice}` as VariantKey;
  const activeVariantCanvas = slide.variants[activeVariantKey]?.canvas;
  // Vídeo de fundo da variante ativa. O snapshot do canvas (previewDataUrl) é
  // transparente quando há vídeo, então o renderizamos atrás da imagem — igual
  // ao CanvasStage do editor.
  const backgroundVideo = slide.variants[activeVariantKey]?.backgroundVideo;

  const [previewDataUrl, setPreviewDataUrl] = useState('');
  // URL do vídeo cujo primeiro frame já está pronto para tocar. Mantém o overlay
  // no lugar do espaço preto enquanto o <video> ainda está bufferizando.
  const [readyVideoUrl, setReadyVideoUrl] = useState<string>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeVariantCanvas e canvasLoadVersion são gatilhos de re-captura — sem eles o snapshot nunca é recapturado quando o conteúdo chega
  useEffect(() => {
    // Double-rAF: first frame lets Fabric finish requestRenderAll, second captures the result
    let raf2: number;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPreviewDataUrl(ctx.getPreviewDataUrl());
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // activeVariantCanvas: re-executa quando o conteúdo do canvas da variante ativa muda
    // (load inicial da API, edições, troca de slide, troca de idioma/device)
    // canvasLoadVersion: re-executa após cada loadFromJSON completar — garante que
    // a captura ocorre depois do canvas renderizar, não antes (race condition de troca de viewport)
  }, [ctx.getPreviewDataUrl, activeVariantCanvas, ctx.canvasLoadVersion]);

  // Mostra loading quando o JSON da variante está sendo buscado da API, ou quando
  // já há canvas em memória mas o snapshot ainda não foi capturado (rAF pendente).
  // Evita o empty state enganoso enquanto o conteúdo ainda não apareceu.
  const showLoading = ctx.isCanvasLoading || (!!activeVariantCanvas && !previewDataUrl);
  // Vídeo de fundo ainda não pronto — mantém o overlay até `onCanPlay` para evitar o espaço preto.
  const isVideoLoading = !!backgroundVideo && readyVideoUrl !== backgroundVideo;

  const DeviceIcon = activeDevice === 'desktop' ? ComputerDesktopIcon : DevicePhoneMobileIcon;
  const dimensionText =
    activeDevice === 'desktop'
      ? `${CANVAS_DIMENSIONS.desktop.width.toString()} × ${CANVAS_DIMENSIONS.desktop.height.toString()} — Desktop`
      : `${CANVAS_DIMENSIONS.mobile.width.toString()} × ${CANVAS_DIMENSIONS.mobile.height.toString()} — Mobile`;

  const isDesktop = activeDevice === 'desktop';

  return (
    <div className="flex flex-col items-center gap-nano p-nano">
      <p className="text-center text-ctx-content-placeholder ts-caption-xxxs">
        {t('preview.readOnlyLabel')}
      </p>

      {/* Login page mockup */}
      <div
        className={`flex-shrink-0 overflow-hidden rounded-[4px] border-hairline border-ctx-layout-border shadow-level1 ${isDesktop ? 'w-full' : 'w-[130px] self-center'}`}
        style={{ width: isDesktop ? 280 : undefined }}
      >
        {isDesktop ? (
          /* Desktop: split-screen layout */
          <div className="flex h-full">
            {/* Left — canvas preview (65% width) */}
            <div className="relative w-[60%] shrink-0 overflow-hidden bg-base-brand-900">
              {backgroundVideo && (
                <video
                  key={backgroundVideo}
                  src={backgroundVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onCanPlay={() => setReadyVideoUrl(backgroundVideo)}
                  onError={() => setReadyVideoUrl(backgroundVideo)}
                  className="absolute inset-none z-0 h-full w-full object-cover"
                >
                  <track kind="captions" />
                </video>
              )}
              {previewDataUrl ? (
                <img
                  src={previewDataUrl}
                  alt=""
                  className="relative z-10 h-full w-full object-contain"
                />
              ) : (
                <div className="flex h-full items-center justify-center px-quark">
                  <span
                    style={{
                      color: '#ffffff40',
                      fontSize: 8,
                      lineHeight: 1.4,
                      textAlign: 'center',
                    }}
                  >
                    {t('preview.emptyState')}
                  </span>
                </div>
              )}
              {(showLoading || isVideoLoading) && <PreviewLoadingOverlay />}
            </div>

            {/* Right — login form */}
            <div
              className="flex w-[40%] flex-col items-center justify-center bg-base-neutral-white px-nano"
              style={{ gap: 5 }}
            >
              {/* Logo */}
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 900,
                  letterSpacing: -0.5,
                  color: '#0d0d0d',
                }}
              >
                MBX
              </span>

              {/* Email field */}
              <div
                className="flex w-full items-center gap-quark rounded-[4px] border-hairline border-ctx-layout-border bg-base-neutral-white px-quark"
                style={{ height: 14 }}
              >
                <UserIcon
                  style={{
                    width: 7,
                    height: 7,
                    color: '#9ca3af',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 6, color: '#9ca3af' }}>
                  {t('preview.loginMockup.email')}
                </span>
              </div>

              {/* Password field */}
              <div
                className="flex w-full items-center justify-between rounded-[4px] border-hairline border-ctx-layout-border bg-base-neutral-white px-quark"
                style={{ height: 14 }}
              >
                <div className="flex items-center gap-quark">
                  <LockClosedIcon
                    style={{
                      width: 7,
                      height: 7,
                      color: '#9ca3af',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 6, color: '#9ca3af' }}>
                    {t('preview.loginMockup.password')}
                  </span>
                </div>
                <EyeIcon
                  style={{
                    width: 7,
                    height: 7,
                    color: '#9ca3af',
                    flexShrink: 0,
                  }}
                />
              </div>

              {/* Remember me */}
              <div className="flex w-full items-center gap-quark">
                <div
                  className="rounded-[3px] border-hairline border-ctx-layout-border"
                  style={{ width: 7, height: 7, flexShrink: 0 }}
                />
                <span style={{ fontSize: 6, color: '#4b5563' }}>
                  {t('preview.loginMockup.rememberMe')}
                </span>
              </div>

              {/* Submit button */}
              <div
                className="flex w-full items-center justify-center rounded-[3px]"
                style={{ height: 14, backgroundColor: '#111827' }}
              >
                <span style={{ fontSize: 6, color: '#ffffff' }}>
                  {t('preview.loginMockup.submit')}
                </span>
              </div>

              {/* Forgot password */}
              <span style={{ fontSize: 5.5, color: '#374151' }}>
                {t('preview.loginMockup.forgotPassword')}
              </span>
            </div>
          </div>
        ) : (
          /* Mobile: stacked layout (canvas top, form bottom) */
          <div className="flex h-full flex-col-reverse">
            {/* Top — dark/gradient side (canvas preview) */}
            <div
              className="relative w-full overflow-hidden rounded-t-sm"
              style={{
                aspectRatio: `${CANVAS_DIMENSIONS.mobile.width} / ${CANVAS_DIMENSIONS.mobile.height}`,
                background: 'linear-gradient(to bottom, #0d0d0d 0%, #0a0a2e 100%)',
              }}
            >
              {backgroundVideo && (
                <video
                  key={backgroundVideo}
                  src={backgroundVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onCanPlay={() => setReadyVideoUrl(backgroundVideo)}
                  onError={() => setReadyVideoUrl(backgroundVideo)}
                  className="absolute inset-none z-0 h-full w-full object-cover"
                >
                  <track kind="captions" />
                </video>
              )}
              {previewDataUrl ? (
                <img
                  src={previewDataUrl}
                  alt={t('preview.emptyState')}
                  className="relative z-10 h-full w-full"
                  style={{ objectFit: 'fill' }}
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <span style={{ color: '#ffffff40', fontSize: 7 }}>{t('preview.emptyState')}</span>
                </div>
              )}
              {(showLoading || isVideoLoading) && <PreviewLoadingOverlay />}
            </div>

            {/* Bottom — login form */}
            <div
              className="flex flex-col items-center justify-center bg-base-neutral-white px-quark py-mini"
              style={{ gap: 3 }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 900,
                  letterSpacing: -0.5,
                  color: '#0d0d0d',
                }}
              >
                MBX
              </span>

              <div
                className="flex w-full items-center gap-quark rounded-[4px] border-hairline border-ctx-layout-border px-quark"
                style={{ height: 13 }}
              >
                <UserIcon style={{ width: 5, height: 5, color: '#9ca3af' }} />
                <span style={{ fontSize: 5, color: '#9ca3af' }}>
                  {t('preview.loginMockup.email')}
                </span>
              </div>

              <div
                className="flex w-full items-center justify-between rounded-[4px] border-hairline border-ctx-layout-border px-quark"
                style={{ height: 13 }}
              >
                <div className="flex items-center gap-quark">
                  <LockClosedIcon style={{ width: 5, height: 5, color: '#9ca3af' }} />
                  <span style={{ fontSize: 5, color: '#9ca3af' }}>
                    {t('preview.loginMockup.password')}
                  </span>
                </div>
                <EyeIcon style={{ width: 5, height: 5, color: '#9ca3af' }} />
              </div>

              <div
                className="flex w-full items-center justify-center rounded-[4px]"
                style={{ height: 11, backgroundColor: '#111827' }}
              >
                <span style={{ fontSize: 5, color: '#ffffff' }}>
                  {t('preview.loginMockup.submit')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="inline-flex items-center gap-quark rounded-[4px] border-hairline border-ctx-layout-border bg-ctx-layout-body px-nano py-quark">
        <DeviceIcon className="h-micro w-micro shrink-0 text-ctx-content-placeholder" />
        <span className="text-ctx-content-placeholder ts-caption-xxxs">{dimensionText}</span>
      </div>
    </div>
  );
}
